import { Button } from "@/components/ui/button";
import { networkPillLabel } from "@/lib/networkLabels";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCallback, useEffect, useState } from "react";
import { Pause, Play, PencilSimple, Sparkle, MagnifyingGlass, ShieldCheck, Article, ArrowsLeftRight } from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { RSpinBlock } from "@/components/RSpin";
import PauseAuditModal from "../components/PauseAuditModal";
import {
  ArticleAiExtrasFields,
  buildAiExtrasPayload,
} from "../components/ArticleAiExtrasFields";
import TrafficRulesDrawer from "../brands/TrafficRulesDrawer";
import { NO_CAP } from "@/lib/trafficRules";
import { RouteTrafficDialog } from "./RouteTrafficDialog";

const PAGE_SIZE = 20;

function formatAssignedClickCap(ab) {
  const live = ab.click_cap_per_day_effective;
  const ov = ab.click_cap_per_day_override;
  const isOverride = ov !== null && ov !== undefined;
  if (live !== null && live !== undefined) {
    if (Number(live) === NO_CAP) {
      return { label: "no cap", isOverride };
    }
    const label = Number(live) === 0 ? "blocked" : Number(live).toLocaleString();
    return { label, isOverride };
  }
  // Fallback when effective was not attached.
  if (isOverride) {
    if (Number(ov) === NO_CAP) {
      return { label: "no cap", isOverride: true };
    }
    return { label: Number(ov) === 0 ? "blocked" : Number(ov).toLocaleString(), isOverride: true };
  }
  const brand = ab.click_cap_per_day;
  if (brand !== null && brand !== undefined && Number(brand) !== NO_CAP) {
    return { label: Number(brand) === 0 ? "blocked" : `${Number(brand).toLocaleString()} (brand)`, isOverride: false };
  }
  return { label: "pub default", isOverride: false };
}

/**
 * Assigned Brands Table — per-publisher list of brand assignments with
 * inline editing for commission %, split, and a side-button to open the
 * passthrough editor dialog when the row is in passthrough mode.
 * Pure presentational — all state and callbacks live in the parent.
 */
export function AssignedBrandsTable({
  publisherId,
  assignedBrands: assignedBrandsProp = null,
  refreshKey = 0,
  publisherDefaultSplit = 100,
  editingComm,
  editForm,
  setEditForm,
  setEditingComm,
  setEditingPassthrough,
  onUpdateCommission,
  onToggleBrandStatus,
  onChanged,
  editingSplitBrandId = null,
  editingSplitValue = "",
  setEditingSplitBrandId,
  setEditingSplitValue,
  onSaveBrandSplit,
  savingSplit = false,
}) {
  const [healing, setHealing] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [auditRow, setAuditRow] = useState(null);
  const [onlyWithClicks, setOnlyWithClicks] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasClicks, setHasClicks] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchPage = useCallback(async () => {
    if (!publisherId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/publishers/${publisherId}/assignments`, {
        params: {
          page,
          page_size: PAGE_SIZE,
          search: search || undefined,
          only_with_clicks: onlyWithClicks,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      const clicks = Boolean(data.has_clicks);
      setHasClicks(clicks);
      if (!clicks && onlyWithClicks) {
        setOnlyWithClicks(false);
      }
    } catch {
      toast.error("Failed to load assigned brands");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [publisherId, page, search, onlyWithClicks, refreshKey]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const assignedBrands = assignedBrandsProp || items;
  // Per-row "Generate article" pending state — keyed on brand_id so
  // each row's spinner is independent (admin may fire several in a row).
  const [generatingFor, setGeneratingFor] = useState(() => new Set());
  const [generateTarget, setGenerateTarget] = useState(null); // assigned brand row
  const [genUrls, setGenUrls] = useState([""]);
  const [genInstructions, setGenInstructions] = useState("");
  const [trafficRulesBrand, setTrafficRulesBrand] = useState(null);
  const [routeFromBrand, setRouteFromBrand] = useState(null);

  const openGenerateDialog = (ab) => {
    setGenerateTarget(ab);
    setGenUrls([""]);
    setGenInstructions("");
  };

  // Drop the assignment into the auto-article retry queue with a
  // synthetic MANUAL.* cid. The article appears in Admin → Auto-Article
  // immediately as `queued` then `processing` → `published`.
  const handleGenerateArticle = async () => {
    const ab = generateTarget;
    if (!ab) return;
    setGeneratingFor((s) => new Set(s).add(ab.brand_id));
    try {
      const payload = buildAiExtrasPayload(genUrls, genInstructions);
      const r = await API.post(
        `/admin/auto-article/publishers/${publisherId}/brands/${encodeURIComponent(ab.brand_id)}/generate-article`,
        payload,
      );
      if (r.data.deduped) {
        toast.info(r.data.message);
      } else {
        toast.success(
          `Queued: ${ab.brand_name}. Cid: ${r.data.conversion_id}. ` +
          `Track in Admin → Auto-Article.`
        );
      }
      setGenerateTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue article");
    } finally {
      setGeneratingFor((s) => {
        const n = new Set(s); n.delete(ab.brand_id); return n;
      });
    }
  };

  const currentPage = Math.max(1, Math.min(page, Math.max(1, totalPages)));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = items;
  const goToPage = (n) => setPage(Math.max(1, Math.min(Math.max(1, totalPages), n)));

  const healRates = async () => {
    if (!window.confirm(
      "Heal rates for any assignments where Our % is 0 by re-pulling the canonical rate from other pubs. " +
      "This won't touch rows that already have non-zero rates. Continue?"
    )) return;
    setHealing(true);
    try {
      const { data } = await API.post("/admin/coverage/heal-rates", {
        publisher_id: publisherId,
        only_zero_rates: true,
      });
      const parts = [`${data.healed} healed`];
      if (data.skipped_already_correct) parts.push(`${data.skipped_already_correct} already correct`);
      if (data.skipped_no_canonical) parts.push(`${data.skipped_no_canonical} no canonical rate found`);
      toast.success(parts.join(" · "));
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Heal failed");
    } finally {
      setHealing(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-900" data-testid="assigned-brands-heading">
          Assigned Brands ({total.toLocaleString()})
        </h3>
        <div className="flex items-center gap-3 flex-1 max-w-2xl">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search brand, advertiser, network…"
              className="h-8 pl-7 text-xs"
              data-testid="assigned-brands-search"
            />
          </div>
          <label
            className="flex items-center gap-2 text-xs text-slate-700 select-none whitespace-nowrap cursor-pointer"
            title={hasClicks ? "Hide brands with no click activity in the last 90 days" : "No brand has click activity yet for this pub"}
            data-testid="assigned-brands-only-clicks-toggle"
          >
            <input
              type="checkbox"
              checked={onlyWithClicks}
              disabled={!hasClicks}
              onChange={(e) => { setOnlyWithClicks(e.target.checked); setPage(1); }}
              className="rounded"
            />
            Only brands with clicks
          </label>
        </div>
        {publisherId && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={healRates}
            disabled={healing}
            data-testid="heal-rates-btn"
            title="Re-pull canonical rates for any 0% rows from other pubs' assignments"
          >
            <Sparkle size={12} className="mr-1" />
            {healing ? "Healing…" : "Heal 0% rates"}
          </Button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-200">
          <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Our %</th>
          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Pub % / Offer</th>
          <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Split</th>
          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Effective</th>
          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Click cap</th>
          <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Actions</th>
        </tr></thead>
        <tbody>
          {loading && (
            <tr><td colSpan={8} className="px-4 py-8">
              <RSpinBlock label="Loading assigned brands" height={160} />
            </td></tr>
          )}
          {!loading && pageRows.map(ab => {
            const split = ab.pub_split_pct ?? 80;
            const network = ab.network || "impact";
            const isSkim = network === "skimlinks";
            const mode = ab.payout_mode || (isSkim ? "passthrough" : "fixed_pct");
            const isFlat = mode === "flat_cpa";
            const isPassthrough = mode === "passthrough";
            const isCpc = mode === "cpc" || (ab.offer_type || "").toLowerCase() === "cpc";
            // Fixed-rate cap rows = `payout_mode=fixed_pct` + `pub_split_pct=100`.
            // These were converted from passthrough via the modal's cap toggle.
            // Distinguishing them lets us:
            //   1. Render a "CAP" badge so admin sees the row originated as
            //      passthrough (vs a Best-Buy-style fixed_pct + split row).
            //   2. Re-surface the dialog pencil so admin can flip back to
            //      passthrough without going through the inline rate editor.
            const isFixedCap = mode === "fixed_pct" && Number(ab.pub_split_pct || 0) === 100;
            const flat = ab.pub_flat_amount || 0;
            // Effective = what the pub actually earns per typical action.
            //   fixed_pct  → pub_pct × split / 100 (% of sale)
            //   flat_cpa   → flat × split / 100 ($ per action)
            //   passthrough → no preview (depends on each network payout)
            //   cpc        → variable (depends on upstream CPC × split)
            let effective = null;
            if (isFlat) {
              effective = (flat * split / 100).toFixed(2);
            } else if (!isPassthrough && !isCpc) {
              effective = ((ab.custom_commission_pct || 0) * split / 100).toFixed(2);
            }
            const isEditing = editingComm === ab.brand_id;
            return (
              <tr key={ab.brand_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{ab.brand_name}</span>
                    {(ab.clicks_count || 0) > 0 && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200"
                        title={`${ab.clicks_count} click${ab.clicks_count === 1 ? "" : "s"} in the last 90 days for this pub`}
                        data-testid={`brand-clicks-badge-${ab.brand_id}`}
                      >
                        {ab.clicks_count.toLocaleString()} clk
                      </span>
                    )}
                    {/* Feed-partner pill — visible on every row so admin
                        sees the source network at a glance (was only
                        shown for Skimlinks before). Mirrors the palette
                        used in AdminBrands / AdminBrandsSync. */}
                    {(() => {
                      const NET_CFG = {
                        impact: "bg-slate-100 text-slate-700",
                        partnerize: "bg-indigo-50 text-indigo-700",
                        skimlinks: "bg-orange-50 text-orange-700",
                        mrge: "bg-emerald-50 text-emerald-700",
                        cj: "bg-amber-50 text-amber-700",
                        pubadhub: "bg-cyan-50 text-cyan-700",
                        pubadhub_kevin: "bg-teal-50 text-teal-700",
                        admedia: "bg-rose-50 text-rose-700",
                        yieldkit: "bg-fuchsia-50 text-fuchsia-700",
                        flexoffers: "bg-violet-50 text-violet-700",
                        shopnomix_kevin: "bg-pink-50 text-pink-700",
                        shopnomix: "bg-lime-50 text-lime-700",
                        siteplug: "bg-blue-50 text-blue-700",
                        maxbounty: "bg-cyan-50 text-cyan-700",
                        cashbackhive: "bg-teal-50 text-teal-700",
                        fatcoupon: "bg-rose-50 text-rose-700",
                        giddyup: "bg-teal-50 text-teal-700",
                        rga: "bg-red-50 text-red-700",
                        rga_vishy: "bg-red-50 text-red-700",
                        rga_ashok: "bg-pink-50 text-pink-700",
                      };
                      const cls = NET_CFG[network] || "bg-slate-100 text-slate-700";
                      const lbl = networkPillLabel(network);
                      return (
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${cls}`}
                          data-testid={`assigned-brand-network-${ab.brand_id}`}
                        >
                          {lbl}
                        </span>
                      );
                    })()}
                    {isFlat && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700" title="Flat CPA — pub earns a fixed $ amount per action, capped at the network's actual payout">
                        Flat CPA
                      </span>
                    )}
                    {isPassthrough && !isSkim && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700" title="Pass-through — pub earns network payout × split. For variable-rate offers like 'Up to 25%'.">
                        Passthrough
                      </span>
                    )}
                    {isFixedCap && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200"
                        title="Fixed-rate cap — pub earns min(sale × pub rate, network payout). Low-rate sales pass through; high-rate sales are capped at the configured pub rate (we keep the difference)."
                        data-testid={`cap-badge-${ab.brand_id}`}
                      >
                        CAP
                      </span>
                    )}
                    {isCpc && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-teal-100 text-teal-800 border border-teal-200"
                        title="CPC offer — pub earns their split of the upstream CPC per click. No conversion required."
                        data-testid={`cpc-badge-${ab.brand_id}`}
                      >
                        CPC
                      </span>
                    )}
                    {ab.traffic_remap?.to_brand_id && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-sky-50 text-sky-800 border border-sky-200"
                        title={`Clicks on this brand_id route to ${ab.traffic_remap.to_network || "target"} ${ab.traffic_remap.to_brand_id}`}
                        data-testid={`traffic-remap-badge-${ab.brand_id}`}
                      >
                        Rerouted → {ab.traffic_remap.to_network || ab.traffic_remap.to_brand_id}
                      </span>
                    )}
                    {ab.is_cashback && (
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200"
                        title="Cashback — the merchant requires the commission to go back to the end consumer. Margin for arbitrage is ~0; verify your terms before relying on this rate for publisher revenue."
                        data-testid={`cashback-badge-${ab.brand_id}`}
                      >
                        Cashback
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{ab.advertiser_name}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono text-slate-500">
                  {isCpc || isSkim || isFlat ? (
                    <span className="text-slate-400 text-xs">—</span>
                  ) : isPassthrough ? (
                    // For Smart-Add (passthrough) rows we still want to
                    // surface the brand's headline rate so admin can scan
                    // the column without opening every dialog. Prefer
                    // the structured base %, fall back to range max %,
                    // then to the network's raw payout_display string.
                    (() => {
                      const pct = Number(ab.base_commission_pct || 0);
                      const maxPct = Number(ab.advertised_max_pct || 0);
                      if (pct > 0) return <span>{pct}%</span>;
                      if (maxPct > 0) return <span>{maxPct}%</span>;
                      if (ab.payout_display) return <span className="text-slate-400 text-xs">{ab.payout_display}</span>;
                      return <span className="text-slate-400 text-xs">—</span>;
                    })()
                  ) : isEditing ? (
                    <Input type="number" step="0.1" value={editForm.base} onChange={e => setEditForm({ ...editForm, base: e.target.value })} className="w-16 h-7 text-right font-mono text-sm inline-block" />
                  ) : `${ab.base_commission_pct ?? 0}%`}
                </td>
                <td className="px-4 py-3 text-right">
                  {isCpc ? (
                    <button
                      type="button"
                      className="text-teal-700 text-xs font-mono cursor-pointer hover:underline inline-flex items-center gap-1"
                      title="CPC payout — click to edit label, pub flat $, and split"
                      onClick={() => setEditingPassthrough({ ...ab, _cpcMode: true })}
                      data-testid={`cpc-edit-btn-${ab.brand_id}`}
                    >
                      <span>
                        {ab.cpc_payout_display || ab.headline_payout || ab.payout_display || "Set CPC label"}
                        {Number(flat) > 0 ? ` · $${Number(flat).toFixed(2)}` : ""}
                      </span>
                      <PencilSimple size={12} />
                    </button>
                  ) : isSkim ? (
                    <span className="text-slate-500 text-xs font-mono" title={ab.payout_display || "See offer details"}>
                      {ab.payout_display || "See details"}
                    </span>
                  ) : isFlat ? (
                    <span className="font-mono text-emerald-700" title="Flat CPA per action">
                      ${Number(flat).toFixed(2)}
                    </span>
                  ) : isPassthrough ? (
                    <button
                      type="button"
                      className="text-purple-700 text-xs font-mono cursor-pointer hover:underline inline-flex items-center gap-1"
                      title="Pass-through — click to view / edit per-action rates"
                      onClick={() => setEditingPassthrough(ab)}
                      data-testid={`pt-edit-btn-${ab.brand_id}`}
                    >
                      {/* Prefer the admin-edited headline (per-pub override
                          set via this dialog) so it actually sticks once
                          saved — matches the precedence used everywhere else
                          (payoutDisplay.js, BrandRow.js, public_api.py).
                          `payout_display` is catalog-synced (see
                          sync-passthrough-rates / crud_status.py brand_map
                          enrichment) and gets silently re-copied from the
                          brand on every fetch, so letting it win here made
                          per-pub headline edits look like they weren't
                          saving. Falls back to the catalog string, then the
                          generic label for ancient assignments with neither. */}
                      {ab.headline_payout || ab.payout_display || "Pass-through"}
                      <PencilSimple size={12} />
                    </button>
                  ) : isEditing ? (
                    <Input type="number" step="0.1" value={editForm.pub} onChange={e => setEditForm({ ...editForm, pub: e.target.value })} className="w-16 h-7 text-right font-mono text-sm inline-block" />
                  ) : isFixedCap ? (
                    // Cap-mode rows open the passthrough dialog (with cap
                    // toggle pre-flipped on) so admin can change the cap
                    // value or revert to passthrough without going through
                    // the inline rate editor (which doesn't expose the
                    // toggle).
                    <button
                      type="button"
                      className="text-blue-700 text-xs font-mono cursor-pointer hover:underline inline-flex items-center gap-1"
                      title="Fixed-rate cap — click to edit cap % or revert to passthrough"
                      onClick={() => setEditingPassthrough(ab)}
                      data-testid={`cap-edit-btn-${ab.brand_id}`}
                    >
                      {ab.custom_commission_pct}%
                      <PencilSimple size={12} />
                    </button>
                  ) : (
                    <span className="font-mono text-[#002FA7] cursor-pointer hover:underline" onClick={() => { setEditingComm(ab.brand_id); setEditForm({ base: String(ab.base_commission_pct ?? 0), pub: String(ab.custom_commission_pct ?? 0), split: String(split) }); }}>
                      {ab.custom_commission_pct}% <PencilSimple size={12} className="inline ml-1" />
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {isEditing ? (
                    <Input type="number" step="1" value={editForm.split} onChange={e => setEditForm({ ...editForm, split: e.target.value })} className="w-16 h-7 text-center font-mono text-sm inline-block" />
                  ) : editingSplitBrandId === ab.brand_id ? (
                    <div className="inline-flex items-center gap-1 justify-center">
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        max="100"
                        value={editingSplitValue}
                        onChange={(e) => setEditingSplitValue(e.target.value)}
                        className="w-14 h-7 text-center font-mono text-sm"
                        data-testid={`split-edit-input-${ab.brand_id}`}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") onSaveBrandSplit?.(ab.brand_id);
                          if (e.key === "Escape") {
                            setEditingSplitBrandId?.(null);
                            setEditingSplitValue?.("");
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs px-2"
                        disabled={savingSplit}
                        onClick={() => onSaveBrandSplit?.(ab.brand_id)}
                        data-testid={`split-save-${ab.brand_id}`}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs px-1"
                        disabled={savingSplit}
                        onClick={() => {
                          setEditingSplitBrandId?.(null);
                          setEditingSplitValue?.("");
                        }}
                      >
                        ✕
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="inline-flex flex-col items-center gap-0.5 font-mono text-xs text-[#002FA7] cursor-pointer hover:underline"
                      onClick={() => {
                        setEditingComm?.(null);
                        setEditingSplitBrandId?.(ab.brand_id);
                        setEditingSplitValue?.(String(split));
                      }}
                      title="Edit this brand's split (does not change publisher default)"
                      data-testid={`split-edit-btn-${ab.brand_id}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {split}/{100 - split}
                        <PencilSimple size={10} />
                      </span>
                      {Math.abs(Number(split) - Number(publisherDefaultSplit)) > 1e-9 && (
                        <span
                          className="inline-block px-1 py-0 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200"
                          title={`Differs from publisher default ${publisherDefaultSplit}/${100 - publisherDefaultSplit}`}
                          data-testid={`split-custom-badge-${ab.brand_id}`}
                        >
                          custom
                        </span>
                      )}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700">
                  {effective !== null
                    ? (isFlat ? `$${effective}` : `${effective}%`)
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {(() => {
                    const capInfo = formatAssignedClickCap(ab);
                    return (
                      <button
                        type="button"
                        className={`text-xs font-mono text-right hover:underline ${
                          capInfo.isOverride ? "text-[#002FA7] font-semibold" : "text-slate-600"
                        }`}
                        title="Edit daily click cap override for this publisher × brand"
                        onClick={() => setTrafficRulesBrand({
                          ...ab,
                          campaign_id: ab.brand_id,
                          name: ab.brand_name,
                        })}
                        data-testid={`open-assigned-click-cap-${ab.brand_id}`}
                      >
                        {capInfo.label}
                        <PencilSimple size={11} className="inline ml-1 opacity-60" />
                      </button>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-center"><span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ab.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{ab.status}</span></td>
                <td className="px-4 py-3 text-right">
                  {isEditing ? (
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onUpdateCommission(ab.brand_id)}>Save</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingComm(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onToggleBrandStatus(ab.brand_id, ab.status)} data-testid={`toggle-pause-${ab.brand_id}`}>
                        {ab.status === "active" ? <><Pause size={14} className="mr-1" /> Pause</> : <><Play size={14} className="mr-1" /> Activate</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        title="Route clicks on this brand to another network brand"
                        onClick={() => setRouteFromBrand(ab)}
                        data-testid={`route-traffic-${ab.brand_id}`}
                      >
                        <ArrowsLeftRight size={14} className="mr-1" /> Route
                      </Button>
                      {ab.status === "active" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                          onClick={() => openGenerateDialog(ab)}
                          disabled={generatingFor.has(ab.brand_id)}
                          title="Generate an SEO article for this brand and queue it in the Auto-Article pipeline"
                          data-testid={`generate-article-${ab.brand_id}`}
                        >
                          <Article size={14} className={"mr-1 " + (generatingFor.has(ab.brand_id) ? "animate-pulse" : "")} />
                          {generatingFor.has(ab.brand_id) ? "Queuing…" : "Generate Article"}
                        </Button>
                      )}
                      {ab.status === "paused" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-slate-500 hover:text-emerald-700"
                          onClick={() => setAuditRow({ brand_id: ab.brand_id, brand_name: ab.brand_name })}
                          title="Verify whether the tracker is actually blocking traffic to this paused brand"
                          data-testid={`audit-pause-${ab.brand_id}`}
                        >
                          <ShieldCheck size={14} className="mr-1" /> Audit
                        </Button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
          {!loading && pageRows.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-400">
              {total === 0
                ? (search.trim() ? `No brands match "${search.trim()}"` : "No brands assigned")
                : "No brands on this page"}
            </td></tr>
          )}
        </tbody>
      </table>
      {total > PAGE_SIZE && (
        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between text-xs text-slate-600" data-testid="assigned-brands-pagination">
          <span className="font-mono">
            {total === 0 ? "0" : `${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, total)}`} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={currentPage <= 1 || loading} onClick={() => goToPage(currentPage - 1)} data-testid="assigned-brands-prev">
              Prev
            </Button>
            <span className="font-mono mx-2">Page {currentPage} / {Math.max(1, totalPages)}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={currentPage >= totalPages || loading} onClick={() => goToPage(currentPage + 1)} data-testid="assigned-brands-next">
              Next
            </Button>
          </div>
        </div>
      )}
      <PauseAuditModal
        open={!!auditRow}
        onClose={() => setAuditRow(null)}
        publisherId={publisherId}
        brandId={auditRow?.brand_id}
        brandName={auditRow?.brand_name}
      />

      <RouteTrafficDialog
        open={!!routeFromBrand}
        onClose={() => setRouteFromBrand(null)}
        sourcePublisherId={publisherId}
        fromBrand={routeFromBrand}
        assignedBrands={assignedBrands}
        onDone={() => onChanged?.()}
      />

      <Dialog
        open={!!generateTarget}
        onOpenChange={(open) => { if (!open) setGenerateTarget(null); }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="generate-article-dialog">
          <DialogHeader>
            <DialogTitle>Generate article</DialogTitle>
            <DialogDescription>
              Queue an AI article for{" "}
              <strong>{generateTarget?.brand_name}</strong>. Add official
              shipping / returns / membership URLs for a full guide. Approve
              updates an existing WordPress post for this brand when one exists.
            </DialogDescription>
          </DialogHeader>
          <ArticleAiExtrasFields
            referenceUrls={genUrls}
            setReferenceUrls={setGenUrls}
            instructions={genInstructions}
            setInstructions={setGenInstructions}
            idPrefix="gen-article"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setGenerateTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleGenerateArticle}
              disabled={generateTarget && generatingFor.has(generateTarget.brand_id)}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="generate-article-submit"
            >
              {generateTarget && generatingFor.has(generateTarget.brand_id)
                ? "Queuing…"
                : "Generate Article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TrafficRulesDrawer
        open={!!trafficRulesBrand}
        onClose={() => setTrafficRulesBrand(null)}
        scope="publisher"
        publisherId={publisherId}
        brand={trafficRulesBrand}
        onSaved={() => {
          setTrafficRulesBrand(null);
          if (onChanged) onChanged();
        }}
      />
    </div>
  );
}
